import { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE_URL } from "../../../config/env";
import { respectCardsPlayerPaths as P } from "../api/apiPaths";
import type {
  LobbyParticipant,
  LobbySnapshot,
  LobbyStreamEvent,
  PublicPullResult,
} from "../types/cardTypes";

/** A reveal/solo-pull event surfaced for the consumer to animate once. */
export interface RevealAnimation {
  userId: string;
  username: string;
  revealIndex: number | null;
  pullResult: PublicPullResult | null;
  /** monotonically increasing so the consumer can dedupe/replay. */
  seq: number;
}

/** A jackpot event surfaced for the consumer to celebrate once. */
export interface JackpotAnimation {
  userId: string;
  username: string;
  cardId: string | null;
  rarity: PublicPullResult["rarity"];
  seq: number;
}

export interface UseDropLobbyStream {
  /** Best-known live lobby state (seeded by the authed snapshot + SSE frames). */
  lobby: LobbySnapshot | null;
  connected: boolean;
  /** Bumps whenever a transition needs an authoritative re-fetch (modifiers/odds). */
  refreshKey: number;
  /** Latest reveal_result / solo_pull, for the consumer to animate. */
  revealAnim: RevealAnimation | null;
  /** Latest jackpot event, for the consumer to celebrate. */
  jackpotAnim: JackpotAnimation | null;
  /** Merge an authoritative (authed) snapshot into the live state. */
  applySeed: (seed: LobbySnapshot | null) => void;
}

/**
 * cards-drop-lobby — subscribes to the lobby SSE stream and reduces the named
 * events into a live `LobbySnapshot`. `EventSource` cannot send the Bearer
 * token, so the stream is viewer-blind: callers recompute "is this me" by
 * comparing event `userId`s against the signed-in user's backend id, and seed
 * the viewer-aware fields (modifiers, odds, `youAreBlocked`) from the authed
 * `getLobbyToday` query via {@link applySeed}. `EventSource` auto-reconnects on
 * drop; we only track connection state for the UI.
 */
export function useDropLobbyStream(): UseDropLobbyStream {
  const [lobby, setLobby] = useState<LobbySnapshot | null>(null);
  const [connected, setConnected] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [revealAnim, setRevealAnim] = useState<RevealAnimation | null>(null);
  const [jackpotAnim, setJackpotAnim] = useState<JackpotAnimation | null>(null);
  const seqRef = useRef(0);

  const applySeed = useCallback((seed: LobbySnapshot | null) => {
    if (!seed) return;
    setLobby((prev) => {
      if (!prev) return seed;
      // A status change means the authed snapshot is the fresher truth (it
      // carries everyone's just-computed modifiers + the armed turn).
      if (prev.status !== seed.status) return seed;
      // Same status: keep the live turn + per-participant pull results, but
      // refresh the viewer-aware odds/modifiers from the authoritative seed.
      const seedById = new Map(seed.participants.map((p) => [p.userId, p]));
      const merged: LobbyParticipant[] = prev.participants.map((p) => {
        const s = seedById.get(p.userId);
        if (!s) return p;
        return {
          ...p,
          arrivalRank: s.arrivalRank,
          modifiers: s.modifiers,
          slotChances: s.slotChances,
          winChance: s.winChance,
          userEmoji: s.userEmoji,
          isYou: s.isYou,
        };
      });
      for (const s of seed.participants) {
        if (!prev.participants.some((p) => p.userId === s.userId)) merged.push(s);
      }
      return {
        ...prev,
        youAreBlocked: seed.youAreBlocked,
        gatheringEndsAt: seed.gatheringEndsAt ?? prev.gatheringEndsAt,
        participants: merged,
      };
    });
  }, []);

  const reduceEvent = useCallback((ev: LobbyStreamEvent) => {
    switch (ev.type) {
      case "lobby_state":
        setLobby(ev.lobby);
        setRefreshKey((k) => k + 1);
        break;

      case "participant_joined":
        setLobby((prev) => {
          if (!prev) return prev;
          // Merge the joiner into the live roster so the gathering screen can
          // pop the new emoji bubble before the authed snapshot re-fetch lands.
          // `participant_joined` carries no modifiers/slotChances, so we seed a
          // minimal row; applySeed/refetch backfill the viewer-aware fields.
          const exists = prev.participants.some((p) => p.userId === ev.userId);
          const participants = exists
            ? prev.participants.map((p) =>
                p.userId === ev.userId
                  ? {
                      ...p,
                      winChance: ev.winChance,
                      multiplier: ev.multiplier ?? p.multiplier,
                      userEmoji: ev.userEmoji,
                    }
                  : p,
              )
            : [
                ...prev.participants,
                {
                  userId: ev.userId,
                  username: ev.username,
                  arrivalRank: prev.participants.length,
                  modifiers: {
                    arrivalBonus: 0,
                    coffeeRatingBonus: 0,
                    creatorBonus: 0,
                    droughtBonus: 0,
                  },
                  slotChances: [],
                  pullStatus: "waiting" as const,
                  pullResult: null,
                  isLateJoiner: ev.isLateJoiner,
                  multiplier: ev.multiplier ?? 1,
                  bestRarity: null,
                  bestRarityChance: null,
                  winChance: ev.winChance,
                  userEmoji: ev.userEmoji,
                  isYou: false,
                },
              ];
          return {
            ...prev,
            participantCount: ev.participantCount,
            gatheringEndsAt: ev.gatheringEndsAt ?? prev.gatheringEndsAt,
            participants,
          };
        });
        break;

      case "odds_update":
        // A join diluted the field — merge each refreshed winChance by userId,
        // immutably so React re-renders and the CSS transition animates the
        // percentage downward.
        setLobby((prev) => {
          if (!prev) return prev;
          const byId = new Map(ev.participants.map((p) => [p.userId, p.winChance]));
          const participants = prev.participants.map((p) =>
            byId.has(p.userId) ? { ...p, winChance: byId.get(p.userId)! } : p,
          );
          return { ...prev, totalSupply: ev.totalSupply, participants };
        });
        break;

      case "gathering_end":
        setLobby((prev) =>
          prev ? { ...prev, participantCount: ev.participantCount } : prev,
        );
        // Odds + the first armed turn arrive only on the authed snapshot.
        setRefreshKey((k) => k + 1);
        break;

      case "reveal_turn":
        setLobby((prev) => {
          if (!prev) return prev;
          const participants = prev.participants.map((p) =>
            p.userId === ev.userId
              ? { ...p, pullStatus: "pending" as const }
              : p,
          );
          return {
            ...prev,
            status: "revealing",
            revealIndex: ev.revealIndex,
            turnWindowMs: ev.turnWindowMs,
            currentTurn: {
              revealIndex: ev.revealIndex,
              userId: ev.userId,
              username: ev.username,
              turnStartedAt: ev.turnStartedAt,
              turnEndsAt: ev.turnEndsAt,
            },
            participants,
          };
        });
        break;

      case "reveal_result":
      case "solo_pull": {
        const revealIndex = ev.type === "reveal_result" ? ev.revealIndex : null;
        setLobby((prev) => {
          if (!prev) return prev;
          const participants = prev.participants.map((p) =>
            p.userId === ev.userId
              ? { ...p, pullStatus: "done" as const, pullResult: ev.pullResult }
              : p,
          );
          return { ...prev, participants };
        });
        seqRef.current += 1;
        setRevealAnim({
          userId: ev.userId,
          username: ev.username,
          revealIndex,
          pullResult: ev.pullResult,
          seq: seqRef.current,
        });
        break;
      }

      case "reveal_complete":
        setLobby((prev) => (prev ? { ...prev, status: "done" } : prev));
        setRefreshKey((k) => k + 1);
        break;

      case "jackpot":
        seqRef.current += 1;
        setJackpotAnim({
          userId: ev.userId,
          username: ev.username,
          cardId: ev.cardId,
          rarity: ev.rarity,
          seq: seqRef.current,
        });
        break;

      default:
        break;
    }
  }, []);

  useEffect(() => {
    const source = new EventSource(`${API_BASE_URL}${P.dropLobbyStream}`);

    source.onopen = () => setConnected(true);
    source.onerror = () => setConnected(false); // EventSource retries on its own

    source.onmessage = (event) => {
      let ev: LobbyStreamEvent;
      try {
        ev = JSON.parse(event.data) as LobbyStreamEvent;
      } catch {
        return;
      }
      reduceEvent(ev);
    };

    return () => source.close();
  }, [reduceEvent]);

  return { lobby, connected, refreshKey, revealAnim, jackpotAnim, applySeed };
}
